const axios = require('axios');
const { utils, ethers } = require('ethers');
const { gql, request } = require('graphql-request');
const sigUtil = require('@metamask/eth-sig-util');
const { 
	_getChainSpecificConstants, 
	_extractChainID, 
	customErrors, 
	KNOT_BATCH_AMOUNT, 
	ZERO,
	ONE_GWEI,
	ONE_ETHER,
	CHAIN_ID,
	customWarnings,
	lifecycleStatuses
} = require('./constants.js');
const { getContractInstance } = require('./contracts');
const { _getFinalisedEpochReport, _authenticateReport } = require('./balanceReporting');
const { _generateBLSCredentials } = require('./BLSCredentialGenerator');
const _ = require('lodash');

const _checkKeystorePass = (password) => {

	if (!password) {
		throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	return _.isString(password) && password.length >= 8;
};

const _add0x = (data) => {

	if(!data) {
		throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	if(Buffer.isBuffer(data)) {
		return data;
	}

	if(data instanceof Uint8Array) {
		return '0x' + Buffer.from(data).toString('hex');
	}

	if(_.isString(data)) {
		return (data.indexOf('0x') !== -1) ? data : '0x'.concat(data);		
	}

	return data;
};

const _remove0x = (data) => {

	if(!data) {
		throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	if(Buffer.isBuffer(data)) {
		return data;
	}

	if(data instanceof Uint8Array) {
		return Buffer.from(data).toString('hex');
	}

	if(_.isString(data)) {
		return (data.indexOf('0x') !== -1) ? data.slice(2) : data;
	}

	return data;
};

const _getPersonalSignInitials = async (provider, blsPublicKey, blsSignature, account, typeofWallet=null) => {

	const msg = {
		blsPublicKey,
		blsSignature,
		lifecycleStatus: '1',
		deadline: '0',
	};

	const jsonMsgAsString = JSON.stringify(msg);
	const hexMsgToSign = utils.hexlify(utils.toUtf8Bytes(jsonMsgAsString));
	
	let sig;
	if (!provider) {
		throw new Error('provider not found');
	}
	if(!typeofWallet) {
		sig = await new Promise((resolve, reject) => {
			// @ts-expect-error
			provider.send(
			{
				method: 'personal_sign',
				params: [hexMsgToSign, account],
			},
			(error, response) => {
				if (error) {
					return reject(error);
				}
				return resolve(response.result);
			},
			);
		});
	}
	else {
		// wallet connect uses provider.request instead of provider.send
		sig = await provider.request({
			method: 'personal_sign',
			params: [hexMsgToSign, account]
		});
	}
	
	return {
		json_data: jsonMsgAsString,
		hex_data: hexMsgToSign,
		signature: sig,
	};
};

const _getBatchPersonalSignInitials = async (provider, blsPublicKeys, blsSignatures, account, typeofWallet=null) => {

	const msg = {
		blsPublicKeys,
		blsSignatures,
		lifecycleStatus: '1',
		deadline: '0',
	};

	const jsonMsgAsString = JSON.stringify(msg);
	const hexMsgToSign = utils.hexlify(utils.toUtf8Bytes(jsonMsgAsString));
	
	let sig;
	if (!provider) {
		throw new Error('provider not found');
	}
	if(!typeofWallet) {
		sig = await new Promise((resolve, reject) => {
			// @ts-expect-error
			provider.send(
			{
				method: 'personal_sign',
				params: [hexMsgToSign, account],
			},
			(error, response) => {
				if (error) {
					return reject(error);
				}
				return resolve(response.result);
			},
			);
		});
	}
	else {
		// wallet connect uses provider.request instead of provider.send
		sig = await provider.request({
			method: 'personal_sign',
			params: [hexMsgToSign, account]
		});
	}
	
	return {
		json_data: jsonMsgAsString,
		hex_data: hexMsgToSign,
		signature: sig,
	};
};

const _getPersonalSignInitialsByPrivateKey = async (blsPublicKey, blsSignature, privateKey) => {

	const msg = {
		blsPublicKey,
		blsSignature,
		lifecycleStatus: '1',
		deadline: '0',
	};

	const jsonMsgAsString = JSON.stringify(msg);
	const hexMsgToSign = utils.hexlify(utils.toUtf8Bytes(jsonMsgAsString));
	
	const sig = sigUtil.personalSign({
        privateKey: Buffer.from(_remove0x(privateKey), 'hex'),
        data: hexMsgToSign
    });
	
	return {
		json_data: jsonMsgAsString,
		hex_data: hexMsgToSign,
		signature: sig,
	};
};

const _getBatchPersonalSignInitialsByPrivateKey = async (blsPublicKeys, blsSignatures, privateKey) => {

	const msg = {
		blsPublicKeys,
		blsSignatures,
		lifecycleStatus: '1',
		deadline: '0',
	};

	const jsonMsgAsString = JSON.stringify(msg);
	const hexMsgToSign = utils.hexlify(utils.toUtf8Bytes(jsonMsgAsString));
	
	const sig = sigUtil.personalSign({
        privateKey: Buffer.from(_remove0x(privateKey), 'hex'),
        data: hexMsgToSign
    });
	
	return {
		json_data: jsonMsgAsString,
		hex_data: hexMsgToSign,
		signature: sig,
	};
};

const _nextFile = (fs, path, storage, depositDataFile) => {

	const newTimestamp = depositDataFile.split('-')[1].split('.')[0]*1 + 1;
	const newDepositFile = `deposit_data-${newTimestamp}.json`;
	const newKeystoreFile = `keystore-m_12381_3600_0_0_0-${newTimestamp}.json`;

	if(fs.existsSync(path.join(storage, newDepositFile))) {
		return _nextFile(fs, path, storage, newDepositFile);
	}

	return { depositDataFile: newDepositFile, keystoreFile: newKeystoreFile };
};

const _generateCredentials = async(signer, password, storage) => {

	const credentials = await _generateBLSCredentials(signer, password);

	const isBrowser = ![typeof window, typeof document].includes('undefined');

	if(isBrowser) {
		return credentials;
	}

	if(!storage) {
		const YELLOW = '\x1b[33m%s\x1b[0m';
		console.log(YELLOW, customWarnings.STORAGE_WARNING);

		return credentials;
	}

	const fs = require('fs');
	const path = require('path');

	if(!fs.existsSync(storage)) {
		throw customErrors.INCORRECT_STORAGE_PATH;
	}

	const TIMESTAMP = Math.floor(+ new Date() / 1000);

	let depositDataFile = `deposit_data-${TIMESTAMP}.json`;
	let keystoreFile = `keystore-m_12381_3600_0_0_0-${TIMESTAMP}.json`;

	if(fs.existsSync(path.join(storage, depositDataFile))) {
		({ depositDataFile, keystoreFile } = await _nextFile(fs, path, storage, depositDataFile));
	}

	fs.writeFileSync(path.join(storage, depositDataFile), JSON.stringify(credentials.depositObject));
	fs.writeFileSync(path.join(storage, keystoreFile), JSON.stringify(credentials.keystore));
	console.log('\x1b[32m%s\x1b[0m', `Created ${depositDataFile} and ${keystoreFile} at ${storage} to store the credentials`);
};

const _getValidatorLifecycleStatus = async (signer, blsPublicKey) => {

	blsPublicKey = _add0x(blsPublicKey);

	const contract = (await getContractInstance(signer)).accountManager();

	return contract.blsPublicKeyToLifecycleStatus(blsPublicKey);
};

const _formDepositDataRoot = async(signer, blsPublicKey, blsSignature) => {

	blsPublicKey = _add0x(blsPublicKey);
	blsSignature = _add0x(blsSignature);

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.formDepositDataRoot(blsPublicKey, blsSignature);
};

const _getLastDepositIndex = async (signer, blsPubKey) => {

	const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

	const lookupQuery = gql`
		query getLastDepositIndex($id: String!) {
			stakehouseAccount(id: $id) {
				lastDepositDepositCount
			}
		}
	`;
  
	const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
		{
			id: _add0x(blsPubKey.toLowerCase())
		}
	);
  
	if (!response || !response.stakehouseAccount || !response.stakehouseAccount.lastDepositDepositCount) {
		throw new Error('Invalid response getting lastDepositDepositCount')
	}

	return _.get(response, 'stakehouseAccount.lastDepositDepositCount');
};

const _getDETHBalanceInIndex = async(signer, blsPublicKey) => {

	blsPublicKey = _add0x(blsPublicKey);

  	const contract = (await getContractInstance(signer)).savETHRegistry();
	const indexId = await contract.associatedIndexIdForKnot(blsPublicKey);

	return contract.knotDETHBalanceInIndex(indexId, blsPublicKey);
};

const _getSLOTBalanceInVault = async (signer, stakehouseAddress, userAddress, blsPublicKey) => {

	blsPublicKey = _add0x(blsPublicKey);

  	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.totalUserCollateralisedSLOTBalanceForKnot(
		stakehouseAddress,
		userAddress,
		blsPublicKey
	);
};

const _getsETHAddress = async (signer, stakehouseAddress) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.stakeHouseShareTokens(
		stakehouseAddress
	);
};

const _getsETHBalance = async (signer, stakehouseAddress, userAddress) => {

	const sETHAddress = await _getsETHAddress(signer, stakehouseAddress);
	const contract = (await getContractInstance(signer)).genericERC20Contract(sETHAddress);

	return contract.balanceOf(userAddress);
};

const _getGenericERC20WalletBalance = async (signer, tokenAddress, userAddress) => {

	const contract = (await getContractInstance(signer)).genericERC20Contract(tokenAddress);

	return contract.balanceOf(userAddress);
};

const _currentSlashedAmountOfSLOTForKnot = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.currentSlashedAmountOfSLOTForKnot(
		_add0x(blsPublicKey)
	);
};

const _checkForMultipleOfGwei = (valueInWei) => {

	if(!valueInWei) {
		throw customErrors.UNDEFINED_VALUE;
	}

	if(!ethers.BigNumber.isBigNumber(valueInWei)) {
		valueInWei = ethers.BigNumber.from(valueInWei.toString());
	}

	// throw error if not a multiple
	if(!valueInWei.mod(ONE_GWEI).eq(ZERO)) {
		throw customErrors.NOT_MULTIPLE_OF_GWEI;
	}

	// return value if found to be a multiple
	return valueInWei;
}

const _specialExitFee = async (signer, blsPublicKey) => {

	const chainID = await _extractChainID(signer);
	// specialExitFee for goerli chain is 0
	if(chainID === CHAIN_ID.GOERLI) {
		return ZERO;
	}

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.specialExitFee(
		_add0x(blsPublicKey)
	);
};

const _calculateExitFee = async (signer, balanceReport) => {

	// Execution layer uses values in wei
	// Consensus layer uses values in gwei

	const contract = (await getContractInstance(signer)).accountManager();

	const specialExitFee = await _specialExitFee(signer, balanceReport.report.blsPublicKey);

	// Stakehouse slashing
	const currentAmount = await _currentSlashedAmountOfSLOTForKnot(
		signer,
		balanceReport.report.blsPublicKey
	);

	const activeBalance = ethers.BigNumber.from(balanceReport.report.activeBalance);
	const lastKnownActiveBalance = await contract.getLastKnownActiveBalance(_add0x(balanceReport.report.blsPublicKey));

	const updatedCurrentAmount = currentAmount.add(specialExitFee);

	if (activeBalance.gte(lastKnownActiveBalance)) {
		// Check if currentAmount + specialExitFee >= 1 ether
		// If yes, return the amount, otherwise return 1 ether
		if(updatedCurrentAmount.gte(ONE_ETHER)) {
			return _checkForMultipleOfGwei(updatedCurrentAmount);
		}

		// if currentAmount + specialExitFee < 1 ether && NOT ZERO
		if(!updatedCurrentAmount.eq(ZERO)) {
			return ONE_ETHER;
		}
		
		return ZERO;
	}

	// value in gwei
	const diff = lastKnownActiveBalance.sub(activeBalance);
	
	// convert diff to wei, then add currentAmount and specialExitFee
	const totalFee = diff.mul(ONE_GWEI).add(updatedCurrentAmount);

	// if 0 < totalFee < 1 ether return 1 ether
	if(totalFee.lt(ONE_ETHER) && totalFee.gt(ZERO)) {
		return ONE_ETHER;
	}

	// totalFee > 1
	return _checkForMultipleOfGwei(totalFee);
};

const _getNumberOfCollateralisedSlotOwnersForKnot = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.numberOfCollateralisedSlotOwnersForKnot(
		_add0x(blsPublicKey)
	);
};

const _getStakehouseRedemptionRate = async (signer, stakehouseAddress) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.redemptionRate(
		stakehouseAddress
	);
};

const _getStakehouseExchangeRate = async (signer, stakehouseAddress) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.exchangeRate(
		stakehouseAddress
	);
};

const _getStakehouseSETHRedemptionThreshold = async (signer, stakehouseAddress) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.sETHRedemptionThreshold(
		stakehouseAddress
	);
};

const _getTotalUserCollateralisedSETHBalanceInHouse = async (signer, stakehouseAddress, userAddress) => {

	const contract = (await getContractInstance(signer)).slotSettlementRegistry();

	return contract.totalUserCollateralisedSETHBalanceInHouse(
		stakehouseAddress,
		userAddress
	);
};

const _totalFundsInKNOTQueue = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.stakeHouseMemberQueue(
		_add0x(blsPublicKey)
	);
};

const _associatedIndexIdForKnot = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.associatedIndexIdForKnot(
		_add0x(blsPublicKey)
	);
};

const _indexIdToOwner = async (signer, indexID) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.indexIdToOwner(
		indexID
	);
};

const _rageQuitChecks = async(signer, stakehouseAddress, userAddress, balanceReport) => {

	const getStatusPromise = _getValidatorLifecycleStatus(
		signer, balanceReport.report.blsPublicKey
	);

	const getCollateralKnotsPromise = _getNumberOfCollateralisedSlotOwnersForKnot(
		signer, balanceReport.report.blsPublicKey
	);

	const getLastReportPromise = _getLastKnownReportForKNOT(
		signer, balanceReport.report.blsPublicKey
	);

	const getCollateralisedsETHPromise = _getTotalUserCollateralisedSETHBalanceInHouse(
		signer, stakehouseAddress, userAddress
	);

	const getRedemptionThresholdPromise = _getStakehouseSETHRedemptionThreshold(
		signer, stakehouseAddress
	);

	// dETH balance is the dETH balance in the savETH Index
	const getdETHBalancePromise = _getDETHBalanceInIndex(
		signer, balanceReport.report.blsPublicKey
	);

	// dETH required for isolation of KNOT
	const dETHForIsolationPromise = _dETHRequiredForIsolation(
		signer, balanceReport.report.blsPublicKey
	);

	const slotBalancePromise = _getSLOTBalanceInVault(
		signer, stakehouseAddress, userAddress, balanceReport.report.blsPublicKey
	);

	const getsETHBalancePromise = _getsETHBalance(
		signer, stakehouseAddress, userAddress
	);

	const allPromises = await Promise.allSettled([
		getStatusPromise, getCollateralKnotsPromise, getLastReportPromise, 
		getCollateralisedsETHPromise, getRedemptionThresholdPromise,
		getdETHBalancePromise, dETHForIsolationPromise, slotBalancePromise,
		getsETHBalancePromise
	]).then(
		async(result) => {

			const lifecycleStatus = result[0].value;
			const collateralisedKnotsCount = result[1].value;
			const lastKnownReport = result[2].value;
			const collateralisedSETHBalance  = result[3].value;
			const sETHRedemptionThreshold = result[4].value;
			const dETHBalance = result[5].value;
			const isolationDETH = result[6].value;
			const SLOTBalance = result[7].value;
			const sETHBalance = result[8].value;
			
			if(lifecycleStatus !== lifecycleStatuses.TOKENS_MINTED) {
				throw customErrors.STATUS_NOT_3;
			}

			if(!collateralisedKnotsCount.eq(ethers.BigNumber.from("1"))) {
				throw customErrors.COLLATERALIZED_KNOTS_NOT_ALLOWED;
			}

			if(lastKnownReport.slashed !== balanceReport.report.slashed) {
				throw customErrors.REPORT_SLASH;
			}

			if(collateralisedSETHBalance.lt(sETHRedemptionThreshold)) {
				throw customErrors.COLLATERALIZED_SETH_BALANCE_LT_THRESHOLD;
			}

			/* If the dETH balance is less than isolationdETH and the dETHbalance in the wallet
			** is also less than the isolationdETH then throw error
			** Otherwise, if the dETHbalance is atleast equal to the isolationdETH then
			** check if the user is the owner of the Index
			*/
			if(dETHBalance.lt(isolationDETH)) {

				const dETHContractInstance = (await getContractInstance(signer)).dETHContract();
				const dETHBalanceInWallet = await dETHContractInstance.balanceOf(userAddress);
	
				if(dETHBalanceInWallet.lt(isolationDETH)) {
					throw customErrors.DETH_BALANCE_LT_24;
				}
			}
			else {
				const indexId = await _associatedIndexIdForKnot(
					signer, 
					balanceReport.report.blsPublicKey
				);
				const indexOwner = await _indexIdToOwner(
					signer,
					indexId
				);
				const userAddressLowerCase = userAddress.toLowerCase();
				const indexOwnerLowerCase = indexOwner.toLowerCase();
				if(userAddressLowerCase !== indexOwnerLowerCase) {
					throw customErrors.NOT_THE_INDEX_OWNER;
				}
			}
	
			const MAX_SLOT_BALANCE = ONE_ETHER.mul('4');
			if(SLOTBalance.lt(MAX_SLOT_BALANCE)) {
	
				const slotNeeded = MAX_SLOT_BALANCE - SLOTBalance;
				const ethBalanceInWallet = await signer.getBalance();
				if(ethBalanceInWallet.lt(slotNeeded)) {
					throw customErrors.SLOT_BALANCE_LT_4;
				}
			}
	
			const MIN_S_ETH_BALANCE = ONE_ETHER.mul('12');
			if(sETHBalance.lt(MIN_S_ETH_BALANCE)) {
				throw customErrors.SETH_BALANCE_LT_12;
			}

			return true;
		}
	).catch(
		(error) => {
			throw error;
		}
	);

	return allPromises;
};

const _isBalanceLower = async (signer, beaconNodeURL, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).accountManager();

	const beaconChainReport = await _getFinalisedEpochReport(
		signer,
		beaconNodeURL,
		blsPublicKey
	);

	const authenticatedReport = await _authenticateReport(signer, beaconNodeURL, beaconChainReport);

	if (authenticatedReport && authenticatedReport.report) {
		const currentAdjustedActiveBalanceBN = ethers.BigNumber.from(authenticatedReport.report.activeBalance);
		const lastKnownAdjustedActiveBalance = await contract.getLastKnownActiveBalance(_add0x(authenticatedReport.report.blsPublicKey)); // gwei
		
		if (currentAdjustedActiveBalanceBN.lt(lastKnownAdjustedActiveBalance)) {
			return {
				result: true,
				metadata: {
					contractBalanceInGwei: lastKnownAdjustedActiveBalance,
					beaconBalanceInGwei: currentAdjustedActiveBalanceBN
				},
				report: authenticatedReport
			};
		}
		else {
			return {
				result: false,
				metadata: {
					contractBalanceInGwei: lastKnownAdjustedActiveBalance,
					beaconBalanceInGwei: currentAdjustedActiveBalanceBN
				},
				report: authenticatedReport
			};
		}
	}

	throw customErrors.COULD_NOT_AUTHENTICATE;
};

const _dETHToSavETH = async (signer, amount) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.dETHToSavETH(
		amount
	);
};

const _savETHToDETH = async (signer, amount) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.savETHToDETH(
		amount
	);
};

const _dETHRewardsMintedForKNOT = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.dETHRewardsMintedForKnot(
		_add0x(blsPublicKey)
	);
};

const _dETHRequiredForIsolation = async (signer, blsPublicKey) => {

	const dETHMinted = await _dETHRewardsMintedForKNOT(signer, blsPublicKey);
	const knotBatchAmountInWei = ethers.utils.parseEther(KNOT_BATCH_AMOUNT);
	const knotBatchAmountBN = ethers.BigNumber.from(knotBatchAmountInWei);

	return knotBatchAmountBN.add(dETHMinted);
};

const _savETHRequiredForIsolation = async (signer, blsPublicKey) => {

	const dETHMinted = await _dETHRewardsMintedForKNOT(signer, blsPublicKey);
	const knotBatchAmountInWei = ethers.utils.parseEther(KNOT_BATCH_AMOUNT);
	const knotBatchAmountBN = ethers.BigNumber.from(knotBatchAmountInWei);

	const totaldETH = knotBatchAmountBN.add(dETHMinted);
	return _dETHToSavETH(signer, totaldETH);
};

const _getKNOTNonce = async (signer, blsPublicKey) => {

    const contract = (await getContractInstance(signer)).transactionRouter();

    return contract.blsPublicKeyInternalNonces(
		_add0x(blsPublicKey)
    );
};

const _getLastKnownReportForKNOT = async (signer, blsPublicKey) => {

    const contract = (await getContractInstance(signer)).accountManager();
    
    return contract.getLastKnownStateByPublicKey(
		_add0x(blsPublicKey)
    );
};

const _updateSavETHIndexName = async (signer, indexID, name) => {

	const indexOwner = await _indexIdToOwner(signer, indexID);
	const userAddress = await signer.getAddress();

	const userAddressLowerCase = userAddress.toLowerCase();
	const indexOwnerLowerCase = indexOwner.toLowerCase();
	if(userAddressLowerCase !== indexOwnerLowerCase) {
		throw customErrors.NOT_THE_INDEX_OWNER;
	}

	const contract = (await getContractInstance(signer)).savETHIndexNamingRegistry();

	return contract.updateName(
		indexID,
		name
	);
};

const _topUpSlashedSlot = async (signer, stakehouseAddress, blsPublicKey, recipient, amount, ethValue) => {

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.topUpSlashedSlot(
		stakehouseAddress,
		_add0x(blsPublicKey),
		recipient,
		amount,
		{ value: ethValue }
	);
};

const _topUpSlashedSlotWithOneEth = async (signer, stakehouseAddress, blsPublicKey, recipient) => {

	const amount = await _currentSlashedAmountOfSLOTForKnot(signer, blsPublicKey);

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.topUpSlashedSlot(
		stakehouseAddress,
		_add0x(blsPublicKey),
		recipient,
		amount,
		{ value: ethers.utils.parseEther("1.0") }
	);
};

const _topUpKNOT = async (signer, blsPublicKey, ethValue) => {

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.topUpKNOT(
		_add0x(blsPublicKey),
		{ value: ethValue }
	);
};

const _topUpKNOTWithOneEth = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).transactionRouter();

	return contract.topUpKNOT(
		_add0x(blsPublicKey),
		{ value: ethers.utils.parseEther("1.0") }
	);
};

const _dETHMetadata = async (signer) => {

	const contract = (await getContractInstance(signer)).savETHRegistry();

	return contract.dETHMetadata();
};

const _getStakehouse = async (signer, blsPublicKey) => {

	const contract = (await getContractInstance(signer)).stakehouseUniverse();

	return contract.memberKnotToStakeHouse(_add0x(blsPublicKey));
};

const _isPartOfStakehouse = async (signer, blsPublicKey) => {

	const stakehouseAddress = await _getStakehouse(signer, blsPublicKey);

	return stakehouseAddress !== ethers.constants.AddressZero;
};

module.exports = {
	_checkKeystorePass,
	_add0x,
	_remove0x,
	_getPersonalSignInitials,
	_getBatchPersonalSignInitials,
	_getPersonalSignInitialsByPrivateKey,
	_getBatchPersonalSignInitialsByPrivateKey,
	_generateCredentials,
	_getValidatorLifecycleStatus,
	_formDepositDataRoot,
	_getLastDepositIndex,
	_getDETHBalanceInIndex,
	_getSLOTBalanceInVault,
	_getsETHAddress,
	_getsETHBalance,
	_getGenericERC20WalletBalance,
	_currentSlashedAmountOfSLOTForKnot,
	_specialExitFee,
	_calculateExitFee,
	_getNumberOfCollateralisedSlotOwnersForKnot,
	_getStakehouseRedemptionRate,
	_getStakehouseExchangeRate,
	_getStakehouseSETHRedemptionThreshold,
	_getTotalUserCollateralisedSETHBalanceInHouse,
	_totalFundsInKNOTQueue,
	_associatedIndexIdForKnot,
	_rageQuitChecks,
	_indexIdToOwner,
	_isBalanceLower,
	_dETHToSavETH,
	_savETHToDETH,
	_dETHRewardsMintedForKNOT,
	_dETHRequiredForIsolation,
	_savETHRequiredForIsolation,
	_getKNOTNonce,
	_getLastKnownReportForKNOT,
	_updateSavETHIndexName,
	_topUpSlashedSlot,
	_topUpSlashedSlotWithOneEth,
	_topUpKNOT,
	_topUpKNOTWithOneEth,
	_dETHMetadata,
	_getStakehouse,
	_isPartOfStakehouse
};
